import { useState } from 'react';
import { X, Save, Plus, Pencil } from 'lucide-react';
import apiClient from '../api/client';
import JsonEditor from './JsonEditor';

export interface MemoryItem {
  id: string;
  memory_type: string;
  description: string | null;
  config: Record<string, unknown>;
}

interface MemoryEditorProps {
  memory?: MemoryItem | null;
  profileId: string;
  onSave: () => void;
  onCancel: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  width: '90%',
  maxWidth: 560,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 24,
};

const MEMORY_TYPES = ['episodic', 'semantic', 'procedural', 'hybrid'];

// 从 axios 错误中提取后端 detail 文案
function apiErrorDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  return typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
}

export default function MemoryEditor({ memory, profileId, onSave, onCancel }: MemoryEditorProps) {
  const isEdit = !!memory;
  const [memoryType, setMemoryType] = useState('semantic');
  const [description, setDescription] = useState('');
  const [config, setConfig] = useState<object>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 渲染期调整：memory prop 变化时同步表单（替代原 useEffect）
  const [prevMemory, setPrevMemory] = useState(memory);
  if (prevMemory !== memory) {
    setPrevMemory(memory);
    if (memory) {
      setMemoryType(memory.memory_type);
      setDescription(memory.description || '');
      setConfig(memory.config || {});
    } else {
      setMemoryType('semantic');
      setDescription('');
      setConfig({});
    }
    setFormError(null);
  }

  const handleSubmit = async () => {
    if (!memoryType.trim()) {
      setFormError('记忆类型为必填');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        memory_type: memoryType.trim(),
        description: description.trim() || null,
        config,
      };
      if (isEdit && memory) {
        await apiClient.put(`/profiles/${profileId}/memories/${memory.id}`, payload);
      } else {
        await apiClient.post(`/profiles/${profileId}/memories`, payload);
      }
      onSave();
    } catch (e) {
      setFormError(apiErrorDetail(e) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ margin: 0 }}>
            {isEdit ? <><Pencil size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />编辑记忆策略</> : <><Plus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />新增记忆策略</>}
          </h3>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onCancel}>
            <X size={16} />
          </button>
        </div>

        {formError && (
          <div className="card mb-4" style={{ borderColor: 'var(--color-error)', background: 'rgba(248,81,73,0.1)' }}>
            <p style={{ color: 'var(--color-error)', margin: 0, fontSize: 13 }}>{formError}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              记忆类型 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <select value={memoryType} onChange={(e) => setMemoryType(e.target.value)}>
              {MEMORY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="如：记录用户历次咨询的体重变化轨迹"
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Config (JSON)</label>
            <JsonEditor
              value={config}
              onChange={setConfig}
              rows={8}
              placeholder={`{"active_window": "90d", "compression": "summarize"}`}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button className="primary" onClick={handleSubmit} disabled={saving} style={{ flex: 1 }}>
            <Save size={14} /> {saving ? '保存中...' : '保存'}
          </button>
          <button className="secondary" onClick={onCancel} disabled={saving}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
